import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../lib/cn";

export type TooltipSide = "top" | "right" | "bottom" | "left";

const GAP = 8;

export interface TooltipProps {
  /** The text shown on hover or focus. Keep it to a few words. */
  label: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  /** Set false to disable — e.g. a sidebar tooltip only wanted when collapsed. */
  enabled?: boolean;
  className?: string;
}

/**
 * A label that appears on hover or keyboard focus.
 *
 * Rendered through a portal with fixed positioning rather than absolutely
 * inside the trigger. An in-flow tooltip is clipped by any ancestor with
 * `overflow: hidden` — which includes the collapsed navigation rail, the very
 * place this is most needed.
 *
 * Deliberately not a replacement for a visible label: it exists for controls
 * whose text is hidden by a layout choice. The label is wired up with
 * `aria-describedby` so it still reaches screen readers.
 */
export function Tooltip({
  label,
  children,
  side = "right",
  enabled = true,
  className,
}: TooltipProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  const show = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const anchors: Record<TooltipSide, { top: number; left: number }> = {
      right: { top: rect.top + rect.height / 2, left: rect.right + GAP },
      left: { top: rect.top + rect.height / 2, left: rect.left - GAP },
      top: { top: rect.top - GAP, left: rect.left + rect.width / 2 },
      bottom: { top: rect.bottom + GAP, left: rect.left + rect.width / 2 },
    };
    setPosition(anchors[side]);
  }, [side]);

  const hide = useCallback(() => setPosition(null), []);

  if (!enabled) return <>{children}</>;

  const translate: Record<TooltipSide, string> = {
    right: "translate(0, -50%)",
    left: "translate(-100%, -50%)",
    top: "translate(-50%, -100%)",
    bottom: "translate(-50%, 0)",
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={cn("relative inline-flex", className)}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={position ? id : undefined}
      >
        {children}
      </span>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {position && (
              <motion.span
                id={id}
                role="tooltip"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
                style={{
                  top: position.top,
                  left: position.left,
                  transform: translate[side],
                }}
                className={cn(
                  "pointer-events-none fixed z-[70] whitespace-nowrap rounded-md px-2 py-1",
                  "bg-ink-900 text-xs font-medium text-ink-50 shadow-ir-md dark:bg-ink-800",
                )}
              >
                {label}
              </motion.span>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
